import { Outlet, Navigate, useNavigate } from "react-router-dom";
import { useAuthProviderV2, useRoutesProvider } from "../providers/provider-hooks";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "../components/ui/sidebar";
import { AppSidebar } from "../components/navigation/AppSidebar";
import { useAuthRedirects } from "./hooks/useAuthRedirects";
import { toast } from "react-toastify";
import { defaultToastOptions } from "../toast";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { BandaInLucru } from "../shared/components/BandaInLucru";
import { trebuieReamintitaActualizareaScolii } from "../shared/utils/school-update-reminder";

export default function AuthenticatedLayout() {
  const { userInfo, token } = useAuthProviderV2();
  const { PATHS, PAGES } = useRoutesProvider();
  const location = useLocation();
  const navigate = useNavigate();

  const { redirectTo } = useAuthRedirects();

  // Handle redirects via useNavigate (React Router best practice)
  // Only navigate if we're NOT already on the target path to prevent infinite loops
  useEffect(() => {
    if (redirectTo && location.pathname !== redirectTo) {
      navigate(redirectTo, { replace: true });
    }
  }, [redirectTo, location.pathname]);

  // Show school update reminder
  useEffect(() => {
    if (!userInfo) return;

    const user = userInfo as any;
    const CUTOFF_DATE = new Date(1665100800 * 1000); // Convert Unix seconds to milliseconds
    const isOnSchoolFormPage = location.pathname === PATHS[PAGES.SCHOOL_FORM_V2];
    // Ecranul de reconfirmare a participarii are propriul CTA („Confirm participarea"); un toast
    // de 120 s care cere actualizarea datelor scolii il acopera si il contrazice.
    const isOnSchoolConfirmationPage =
      location.pathname === PATHS[PAGES.SCHOOL_REGISTRATION_CONFIRMATION];

    if (
      trebuieReamintitaActualizareaScolii(user.role, user.isEnabled, user.schoolUpdatedAt, CUTOFF_DATE) &&
      !isOnSchoolFormPage &&
      !isOnSchoolConfirmationPage
    ) {
      // toastId: fara el, un profesor care navigheaza cateva pagini acumuleaza un toast nou de
      // 120s la fiecare navigare (efectul depinde de location.pathname, in deps de mai jos).
      // Cat timp un toast cu acelasi id e activ, react-toastify nu mai creeaza altul.
      toast.info("Vă rugăm actualizați informațiile școlii", {
        ...defaultToastOptions,
        autoClose: 120000,
        toastId: 'school-update-reminder',
      });
    }
  }, [userInfo, location.pathname, PATHS, PAGES]);

  if (!token || !userInfo) {
    return <Navigate to={PATHS[PAGES.LOGIN]} replace />;
  }

  // Poarta pe ecranul de confirmare restrange LISTA de linkuri, in AppSidebar, nu containerul:
  // NavUser din SidebarFooter e singurul logout al aplicatiei, iar pe mobil sidebar-ul e
  // offcanvas, deci fara declansatorul de hamburger nici nu s-ar putea deschide. Ascunderea
  // containerului (pana la 2026-09-03) lasa profesorul in asteptare fara nicio iesire.
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="flex-1 flex flex-col md:overflow-hidden">
        {/* Floating hamburger menu - only on mobile */}
        <div className="md:hidden ecotic-hamburger-floating">
          <SidebarTrigger className="size-5 touch-target" />
        </div>

        {/* Container de flex pentru tiparul de scroll BgContentWrap - NU `<main>`: `SidebarInset`
            de mai sus (components/ui/sidebar.tsx:307) e deja randat ca `<main>`, iar un al doilea
            `<main>` imbricat in interior incalca ARIA (un singur landmark `main`, niciodata
            imbricat) - cititoarele de ecran nu-l pot distinge de cel real. */}
        <div className="flex-1 flex flex-col md:overflow-hidden ecotic-main-content">
          <Outlet />
        </div>
      </SidebarInset>
      <BandaInLucru />
    </SidebarProvider>
  );
}
